/** Market Intel — v1 intelView(): live analytics from the scrapers. Outcome
 * tiles, hottest leads (Deal Engine score ≥40), per-county and per-tier
 * tables. Everything recomputes from state already on the client. */
import { useMemo } from "react";
import { moneyExact } from "../../lib/money";
import type { Lead } from "../../types";
import { natKey } from "../../lib/natkey";
import { deBadgeHue } from "../leads/dealEngine";
import { intelByCounty, intelByTier, intelHeadline, intelHot } from "./api";

const fmt$ = (n: number | null): string => (n ? moneyExact(n) : "—");

export function IntelPage({
  leads,
  edits,
  outcomeLog,
  onOpenLead,
}: {
  leads: Lead[];
  edits: Map<string, Record<string, unknown>>;
  outcomeLog: unknown;
  onOpenLead: (address: string) => void;
}) {
  const now = Date.now();
  const head = useMemo(() => intelHeadline(outcomeLog), [outcomeLog]);
  const counties = useMemo(() => intelByCounty(leads, outcomeLog, now), [leads, outcomeLog, now]);
  const tiers = useMemo(() => intelByTier(leads, outcomeLog, now), [leads, outcomeLog, now]);
  const hot = useMemo(() => intelHot(leads, edits, natKey, outcomeLog, now), [leads, edits, outcomeLog, now]);

  return (
    <div className="intel">
      <div className="anlhero">
        <div className="anlherotext">
          <h1>Market Intel</h1>
          <p>
            Live analytics from your scrapers — {leads.length.toLocaleString()} scraped leads ·{" "}
            {head.tracked.toLocaleString()} tracked outcomes · updates with every scraper poll.
          </p>
        </div>
      </div>

      <div className="inteltiles">
        <div className="cstat blue"><strong>{head.tracked.toLocaleString()}</strong><span>Tracked listings</span></div>
        <div className="cstat violet"><strong>{head.pending.toLocaleString()}</strong><span>Went pending</span></div>
        <div className="cstat amber"><strong>{head.bom.toLocaleString()}</strong><span>Back on market</span></div>
        <div className="cstat emerald"><strong>{head.sold.toLocaleString()}</strong><span>Sold (outcome known)</span></div>
        <div className="cstat slategray"><strong>{head.medianCutPct !== null ? head.medianCutPct.toFixed(1) + "%" : "—"}</strong><span>Median cut size</span></div>
      </div>

      <div className="dcard">
        <div className="dhead">
          <h2>🔥 Hit list — highest-leverage leads right now</h2>
          <span className="sub">Deal Engine score ≥ 40 · top {hot.length}</span>
        </div>
        <p className="intelnote">
          Score = keyword tier + days on market + price cuts + county softness + back-on-market.
          Offer range from the research matrix, capped at MAO (0.72×ARV − rehab) when ARV is known.
        </p>
        {hot.length === 0 ? <div className="sub none">Nothing scoring ≥40 yet — fresh markets are quiet.</div> : null}
        {hot.map((h) => (
          <button key={h.lead.id} className="hotrow" onClick={() => onOpenLead(h.lead.address)}>
            <span className={"descore small de-" + deBadgeHue(h.score)}>{h.score}</span>
            <span className="hotwho">
              <strong>{h.lead.address.split(",")[0]}</strong>
              <span className="sub">
                {(h.lead.county ?? "—").replace(/ county/i, "")} · {h.dom}d · {h.cuts} cut{h.cuts === 1 ? "" : "s"}
                {h.tierName ? " · " + h.tierName : ""}
              </span>
            </span>
            {h.offer ? (
              <span className="hotoffer">
                offer {fmt$(h.offer.lo)}–{fmt$(h.offer.hi)}
                {h.offer.capped ? " · MAO-capped" : ""}
              </span>
            ) : null}
          </button>
        ))}
      </div>

      <div className="dcard">
        <div className="dhead">
          <h2>By county</h2>
          <span className="sub">{counties.length} counties</span>
        </div>
        <div className="inteltablewrap">
          <table className="inteltable">
            <thead>
              <tr>
                <th>County</th><th>Listings</th><th>Med. price</th><th>Med. DOM</th>
                <th>% w/ cut</th><th>Pending</th><th>BOM</th><th>Sold</th><th>Sold/List</th>
              </tr>
            </thead>
            <tbody>
              {counties.map((c) => (
                <tr key={c.county}>
                  <td>
                    {c.county}
                    {c.soft ? <span className="softpill">soft</span> : null}
                  </td>
                  <td>{c.n.toLocaleString()}</td>
                  <td>{fmt$(c.medPrice)}</td>
                  <td>{c.medDom || "—"}</td>
                  <td>{c.cutPct}%</td>
                  <td>{c.pending}</td>
                  <td className="bomcell">{c.bom}</td>
                  <td>{c.sold}</td>
                  <td className="ratio">{c.soldToListPct !== null ? c.soldToListPct + "%" : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="dcard">
        <div className="dhead">
          <h2>By keyword tier</h2>
          <span className="sub">distress-language performance</span>
        </div>
        <div className="inteltablewrap">
          <table className="inteltable">
            <thead>
              <tr><th>Tier</th><th>Listings</th><th>% w/ cut</th><th>Med. DOM</th><th>Sold</th><th>Sold/List</th></tr>
            </thead>
            <tbody>
              {tiers.length === 0 ? (
                <tr><td colSpan={6} className="sub">No keyword hits yet.</td></tr>
              ) : null}
              {tiers.map((t) => (
                <tr key={t.tier}>
                  <td>{t.tier}</td>
                  <td>{t.n.toLocaleString()}</td>
                  <td>{t.cutPct}%</td>
                  <td>{t.medDom || "—"}</td>
                  <td>{t.sold}</td>
                  <td className="ratio">{t.soldToListPct !== null ? t.soldToListPct + "%" : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
